import type { OnboardingEvent } from '@developerz/domain';
import { OnboardingState } from '@developerz/domain';
import { Chip } from '@developerz/ui';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import type { IntendedTier } from '../lib/onboarding';
import { ONBOARDING_STEPS } from '../lib/onboardingSteps';
import type { RepoRow } from '../lib/repos';
import { OnboardingInvalidNotice } from './OnboardingInvalidNotice';
import { OnboardingMcpStep } from './OnboardingMcpStep';
import { OnboardingPanelFooter } from './OnboardingPanelFooter';
import { OnboardingRepoList } from './OnboardingRepoList';
import { OnboardingYmlBlock } from './OnboardingYmlBlock';
import { OrgKeyGateNotice } from './OrgKeyGateNotice';
import { Spinner } from './Spinner';

// The active funnel step's panel — the canvas ONBOARDING artboard's right panel:
// "STEP n OF N", the title, the lead, the step's body, the action row.
// Presenter — the container owns the async (advance / refetch / clipboard / the
// repo walk) and passes callbacks. `active` is handled by the container's done
// card, not here.
//
// Title, lead copy and the primary action all come from `ONBOARDING_STEPS`, so
// the states are enumerated in ONE place and a new one is a compile error there
// rather than a `fallback` in production. There is no catch-all branch left:
// `new` fell through the old one and told a signed-in user "Signing you in…".
//
// Steps parked on a webhook (key verify, GitHub install, yml validate) show a
// spinner beside the wait copy so the page reads as "working", not stalled —
// but each one now ALSO carries an action, because a spinner is not an exit.
//
// A state names a CARD here. The stored ladder runs NEW → AUTHED → PLAN_SELECTED → KEY_SET →
// GH_CONNECTED → BOX_ATTACHED → REPOS_SELECTED → YML_REQUESTED → MCP_CONNECTED → ACTIVE, and the
// person's order is sign in, plan, GitHub (KEY_SET's card), LLM (PLAN_SELECTED's), MCP
// (YML_REQUESTED's), where and how (BOX_ATTACHED's), a server (GH_CONNECTED's), the setup PR:
// `displayedOnboardingState` picks the card from facts (#7802), and a card ahead of the ladder
// never fires its advance (`displayOnly`), since the ladder would refuse it.
//
// `boxStep` is a SLOT, not a branch: enrolling a server needs the live roster,
// which is a query, and this presenter is rendered for every state without a
// query client (`OnboardingPanel.states.test.tsx`). The container owns that
// fetch exactly as it owns the repo walk.
//
// "The two hard ones get the room": the box step and the setup-PR step lay their
// body out across the panel's full width ({@link WIDE_STATES}); every other step
// keeps its body at a reading measure. Only the box step carries the BYOVM chip —
// it is the one step where "your agents and your code run on your boxes" is the
// thing to know.

/** The steps whose body spans the panel: Add a server, Merge the setup PR. */
const WIDE_STATES: ReadonlySet<OnboardingState> = new Set([
  OnboardingState.GhConnected,
  OnboardingState.BoxAttached,
  OnboardingState.ReposSelected,
]);
export type OnboardingPanelProps = {
  readonly state: OnboardingState;
  /** This card is ahead of the stored ladder: its Continue re-reads rather than advances. */
  readonly displayOnly?: boolean;
  /** Where and how: a slot for the same reason `boxStep` is, it is a live query. */
  readonly reposStep?: JSX.Element;
  /** 1-based position of this step on the rail. */
  readonly step: number;
  /** How many steps the rail shows (nine, or ten with the paid-seat step). */
  readonly stepCount: number;
  readonly busy: boolean;
  readonly ghAppInstallUrl: string;
  /** Enabled repos; `undefined` while the walk is in flight OR after it failed. */
  readonly repos: readonly RepoRow[] | undefined;
  /** The walk REFUSED — never folded into "you have none" (`loadedRepos` rule). */
  readonly reposFailed: boolean;
  readonly onRetryRepos: () => void;
  readonly onAdvance: (event: OnboardingEvent, intendedTier?: IntendedTier) => void;
  /** Copy this text, then let the container decide whether that advances. */
  readonly onCopy: (text: string) => void;
  /** Re-read `/v1/onboarding/state` — the webhook-parked steps' only lever. */
  readonly onRecheck: () => void;
  /** The enrol-a-server body (`<OnboardingBoxStep/>`), rendered by the container. */
  readonly boxStep: JSX.Element;
  /**
   * The account holds personal keys and no ORG key — `validOrgKey` needs
   * `user_id IS NULL`, so the key step can never complete on those. False
   * whenever either pool's read has not landed.
   */
  readonly personalKeysOnly: boolean;
};

export function OnboardingPanel(props: OnboardingPanelProps): JSX.Element {
  const spec = () => ONBOARDING_STEPS[props.state];
  /** Only a walk that LANDED empty may hold Continue shut. A failed one must not. */
  const reposBlocked = (): boolean =>
    props.state === OnboardingState.BoxAttached &&
    props.repos !== undefined &&
    props.repos.length === 0;
  const waiting = (text: string): JSX.Element => (
    <p class="onboarding__waiting">
      <Spinner size="1.05rem" />
      {text}
    </p>
  );

  return (
    <section
      class="onboarding-step"
      classList={{ 'onboarding-step--wide': WIDE_STATES.has(props.state) }}
      aria-labelledby="onboarding-step-title"
    >
      <header class="onboarding-step__head">
        <div class="onboarding-step__heading">
          <p class="onboarding-step__count">
            {t('onboarding.step_count', { n: props.step, count: props.stepCount })}
          </p>
          <h2 id="onboarding-step-title" class="onboarding-step__title">
            {t(spec().titleKey)}
          </h2>
        </div>
        <Show when={props.state === OnboardingState.GhConnected}>
          <Chip>{t('onboarding.byovm_chip')}</Chip>
        </Show>
      </header>

      <div class="onboarding-step__body">
        {/* `invalid` states its lead inside the alert, so it isn't repeated here. */}
        <Show when={props.state !== OnboardingState.Invalid}>
          <p class="onboarding-step__lead">{t(spec().bodyKey)}</p>
        </Show>

        <Switch>
          <Match when={props.state === OnboardingState.PlanSelected}>
            {waiting(t('onboarding.keys_waiting'))}
            {/* A personal key satisfies nothing this step waits on, and the
                spinner alone said "working" over an account that was stuck
                (`lib/org-key-gate.ts`). */}
            <Show when={props.personalKeysOnly}>
              <OrgKeyGateNotice message={t('onboarding.keys_personal_only')} />
            </Show>
          </Match>
          <Match when={props.state === OnboardingState.KeySet}>
            {waiting(t('onboarding.connect_waiting'))}
            <p class="onboarding-step__warning">{t('onboarding.connect_token_note')}</p>
          </Match>
          <Match when={props.state === OnboardingState.GhConnected}>{props.boxStep}</Match>
          <Match when={props.state === OnboardingState.BoxAttached}>
            {props.reposStep}
            <OnboardingRepoList
              repos={props.repos}
              failed={props.reposFailed}
              onRetry={props.onRetryRepos}
              ghAppInstallUrl={props.ghAppInstallUrl}
            />
            <Show when={reposBlocked()}>
              <p class="onboarding__gate">{t('onboarding.repos_none_gate')}</p>
            </Show>
          </Match>
          <Match when={props.state === OnboardingState.ReposSelected}>
            <OnboardingYmlBlock busy={props.busy} onCopy={props.onCopy} />
          </Match>
          <Match when={props.state === OnboardingState.YmlRequested}>
            <OnboardingMcpStep connected={false} />
          </Match>
          <Match when={props.state === OnboardingState.McpConnected}>
            <OnboardingMcpStep connected />
            {waiting(t('onboarding.validate_waiting'))}
            <OnboardingYmlBlock busy={props.busy} onCopy={props.onCopy} />
          </Match>
          <Match when={props.state === OnboardingState.Invalid}>
            <OnboardingInvalidNotice repos={props.repos} />
          </Match>
        </Switch>
      </div>

      {/* No `onCopy`: the footer's one clipboard button was the yml step's
          primary action, and that seat now belongs to the pass's pull request
          (arc 101 slice 05 step 6). Both copy buttons live in
          `<OnboardingYmlBlock/>`, beside the text they copy. */}
      <OnboardingPanelFooter
        state={props.state}
        displayOnly={props.displayOnly ?? false}
        busy={props.busy}
        reposBlocked={reposBlocked()}
        ghAppInstallUrl={props.ghAppInstallUrl}
        onAdvance={props.onAdvance}
        onRecheck={props.onRecheck}
      />
    </section>
  );
}
